import { cva } from 'class-variance-authority';
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';

/* -------------------------------------------------------------------------- */
/* context                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Whether moving focus along the strip also selects.
 *
 * `'automatic'` is the default because it is what the ARIA authoring practices
 * recommend when the panels are already there — arrowing across a two-tab
 * edit/preview switch should not need a second keypress to show anything.
 * `'manual'` is for a tab whose panel costs something to reveal, where arrowing
 * past it would fire that cost. In manual mode the tab is a plain `<button>`,
 * so Enter and Space activate it with no keyboard code of ours.
 */
export type TabsActivation = 'automatic' | 'manual';

/** Sizes belong to the whole set, not to one tab, so they live on `Tabs`. */
export type TabSize = 'sm' | 'md';

interface TabsContextValue {
  baseId: string;
  /** `undefined` only in the first render of an uncontrolled set — see `TabList`. */
  value: string | undefined;
  /** A selection the user made: updates internal state and reports it. */
  select: (value: string) => void;
  /** A selection nobody made: adopts a default, silently, uncontrolled only. */
  adopt: (value: string) => void;
  activation: TabsActivation;
  keepMounted: boolean;
  size: TabSize;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const context = useContext(TabsContext);
  if (context === null) {
    throw new Error(`<${component}> must be rendered inside a <Tabs>.`);
  }
  return context;
}

/**
 * Values end up in DOM ids. Whitespace is the one character that genuinely
 * breaks things — `aria-labelledby` is a space-separated list of ids, so a
 * value with a space in it points at two ids that do not exist. Keep values to
 * plain slugs (`'edit'`, `'preview'`); this only stops the obvious foot-gun.
 */
function idSafe(value: string): string {
  return value.replace(/\s+/g, '_');
}

const tabId = (baseId: string, value: string): string => `${baseId}-tab-${idSafe(value)}`;
const panelId = (baseId: string, value: string): string => `${baseId}-panel-${idSafe(value)}`;

/* -------------------------------------------------------------------------- */
/* root                                                                        */
/* -------------------------------------------------------------------------- */

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Controlled selection. Leave undefined to let the Tabs hold its own. */
  value?: string;
  /** Which tab starts selected when uncontrolled. */
  defaultValue?: string;
  /** Fires with the value the user just selected. Never fires for a default. */
  onValueChange?: (value: string) => void;
  /** Default `'automatic'` — arrowing along the strip selects as it goes. */
  activation?: TabsActivation;
  /**
   * Keep every panel mounted and hide the inactive ones, instead of unmounting
   * them. Off by default: an unmounted panel is the one hiding a screen reader
   * cannot see through. Turn it on when a panel holds state that a remount
   * would throw away — scroll position, an `<iframe>` that would re-fetch, a
   * half-typed uncontrolled input.
   */
  keepMounted?: boolean;
  size?: TabSize;
}

/**
 * A tab set: `Tabs` owns the state, `TabList` draws the strip, `Tab` is one
 * button in it and `TabPanel` is what that button reveals.
 *
 *     <Tabs defaultValue="edit">
 *       <TabList label="Template">
 *         <Tab value="edit">Edit</Tab>
 *         <Tab value="preview">Preview</Tab>
 *       </TabList>
 *
 *       <TabPanel value="edit"><BlockEditor … /></TabPanel>
 *       <TabPanel value="preview"><Preview … /></TabPanel>
 *     </Tabs>
 *
 * The four names are the four ARIA roles — `tablist`, `tab`, `tabpanel` — on
 * purpose: what you write is what lands in the accessibility tree.
 *
 * Controlled when you pass `value`, self-managing when you pass `defaultValue`
 * or neither, the same split as `Checkbox` and `Dropzone`. `Checkbox` can fall
 * back to `false`; a tab set has no such literal, so an uncontrolled set with
 * no usable `defaultValue` adopts the first enabled tab instead — see
 * `TabList`. `onValueChange` does not fire for that: nobody chose it.
 */
export const Tabs = forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  {
    value: valueProp,
    defaultValue,
    onValueChange,
    activation = 'automatic',
    keepMounted = false,
    size = 'md',
    className,
    children,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const [internalValue, setInternalValue] = useState<string | undefined>(defaultValue);

  const controlled = valueProp !== undefined;
  const value = controlled ? valueProp : internalValue;

  const context: TabsContextValue = {
    baseId: `wend-tabs-${generatedId}`,
    value,
    select: (next) => {
      if (!controlled) setInternalValue(next);
      onValueChange?.(next);
    },
    adopt: (next) => {
      if (!controlled) setInternalValue(next);
    },
    activation,
    keepMounted,
    size,
  };

  return (
    <TabsContext.Provider value={context}>
      <div ref={ref} className={cn('flex w-full min-w-0 flex-col gap-4', className)} {...rest}>
        {children}
      </div>
    </TabsContext.Provider>
  );
});

/* -------------------------------------------------------------------------- */
/* list                                                                        */
/* -------------------------------------------------------------------------- */

export const tabListVariants = cva(
  cn(
    'inline-flex w-fit max-w-full items-center gap-1 overflow-x-auto',
    'rounded-base border border-line bg-surface-2/60 p-1',
  ),
);

export interface TabListProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Accessible name for the strip. Supply it whenever the surrounding heading
   * does not already say what these tabs switch between — a screen reader
   * announces "tab list" and nothing else without it.
   */
  label?: string;
}

/**
 * `useLayoutEffect` on the client, `useEffect` on the server — the default-tab
 * fallback below has to run before paint, and React warns about layout effects
 * during SSR.
 */
const useIsomorphicLayoutEffect = typeof document === 'undefined' ? useEffect : useLayoutEffect;

/** The tabs a keypress may land on. A disabled `<button>` cannot take focus. */
function enabledTabs(list: HTMLElement): HTMLElement[] {
  return Array.from(list.querySelectorAll<HTMLElement>('[role="tab"]:not([disabled])'));
}

/**
 * The strip. It is also the only thing that knows the tabs in DOM order, so it
 * carries the one piece of behaviour that needs that: picking a default.
 */
export const TabList = forwardRef<HTMLDivElement, TabListProps>(function TabList(
  { label, className, children, ...rest },
  ref,
) {
  const { value, adopt } = useTabsContext('TabList');
  const [list, setList] = useState<HTMLDivElement | null>(null);

  const setRefs = (node: HTMLDivElement | null): void => {
    setList(node);
    if (typeof ref === 'function') ref(node);
    else if (ref !== null && ref !== undefined) ref.current = node;
  };

  // Deliberately no dependency array, like Checkbox's `indeterminate` effect:
  // it early-returns the moment the selection matches a tab, and the set of
  // tabs can change under it — a tab removed while selected would otherwise
  // leave every panel hidden and no tab in the tab order at all.
  useIsomorphicLayoutEffect(() => {
    if (list === null) return;

    const all = Array.from(list.querySelectorAll<HTMLElement>('[role="tab"]'));
    if (value !== undefined && all.some((tab) => tab.dataset.value === value)) return;

    const first = enabledTabs(list)[0];
    const fallback = first?.dataset.value;
    if (fallback !== undefined) adopt(fallback);
  });

  return (
    <div
      ref={setRefs}
      // A tablist is horizontal by default, and this one has no vertical mode,
      // so `aria-orientation` would only restate the default.
      role="tablist"
      aria-label={label}
      className={cn(tabListVariants(), className)}
      {...rest}
    >
      {children}
    </div>
  );
});

/* -------------------------------------------------------------------------- */
/* tab                                                                         */
/* -------------------------------------------------------------------------- */

export const tabVariants = cva(
  cn(
    'relative inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-sm font-medium whitespace-nowrap',
    'text-muted transition-colors duration-150 ease-wend',
    "[&_svg:not([class*='size-'])]:size-4",
    'hover:enabled:bg-white/[0.06] hover:enabled:text-text',
    'disabled:cursor-not-allowed disabled:opacity-55',
    'aria-selected:bg-brand-a/20 aria-selected:text-white',
    'aria-selected:shadow-[inset_0_0_0_1px_rgba(155,94,246,0.35)]',
    // High-contrast mode throws the tint and the inset ring away, which would
    // leave the selected tab looking exactly like the other one. The state is
    // in `aria-selected` either way; this puts it back on the screen.
    'forced-colors:aria-selected:underline',
    focusRing,
  ),
  {
    variants: {
      size: {
        sm: 'px-2.5 py-1 text-[0.8rem]',
        md: 'px-3.5 py-1.5 text-[0.875rem]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

// No `size` here: it belongs to the whole set and comes off `Tabs`, so two tabs
// in one strip cannot end up different heights.
export interface TabProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'type'> {
  /** Ties this tab to the `TabPanel` with the same value. */
  value: string;
}

/** Keys this component owns. Anything else is left to the page. */
const navKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'] as const;

type NavKey = (typeof navKeys)[number];

const isNavKey = (key: string): key is NavKey => (navKeys as readonly string[]).includes(key);

/**
 * One button in the strip. A real `<button>`, so Enter and Space activate it
 * without a keyboard handler and the disabled state is the platform's.
 *
 * Two things make the strip behave like a tab strip rather than a row of
 * buttons, and both are required by the ARIA contract rather than nice to have:
 *
 * 1. **Roving tabindex.** The strip is one tab stop, not one per tab: only the
 *    selected tab is reachable with Tab, the rest are `tabindex="-1"`. So Tab
 *    from the strip goes to the panel, not to the next tab.
 * 2. **Arrow keys.** Left and Right move along it and wrap around; Home and End
 *    jump to the ends. Disabled tabs are skipped — they cannot hold focus.
 *    Up and Down are deliberately not taken: this strip is horizontal, and
 *    stealing them would break scrolling.
 */
export const Tab = forwardRef<HTMLButtonElement, TabProps>(function Tab(
  { value, onClick, onKeyDown, className, children, ...rest },
  ref,
) {
  const { baseId, value: selectedValue, select, activation, keepMounted, size } =
    useTabsContext('Tab');
  const selected = selectedValue === value;

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
    onKeyDown?.(event);
    if (event.defaultPrevented || !isNavKey(event.key)) return;

    const list = event.currentTarget.closest<HTMLElement>('[role="tablist"]');
    if (list === null) return;

    const tabs = enabledTabs(list);
    const index = tabs.indexOf(event.currentTarget);
    if (index === -1) return;

    const target =
      event.key === 'Home'
        ? tabs[0]
        : event.key === 'End'
          ? tabs[tabs.length - 1]
          : event.key === 'ArrowLeft'
            ? tabs[(index - 1 + tabs.length) % tabs.length]
            : tabs[(index + 1) % tabs.length];

    if (target === undefined) return;

    // Home and End scroll the page otherwise, and the strip is not what would
    // scroll.
    event.preventDefault();
    target.focus();

    const targetValue = target.dataset.value;
    if (activation === 'automatic' && targetValue !== undefined) select(targetValue);
  };

  return (
    <button
      ref={ref}
      type="button"
      // The panel of an unselected tab is not in the DOM unless the set keeps
      // its panels mounted, and `aria-controls` pointing at an id that does not
      // exist is a broken reference rather than a helpful one.
      {...(selected || keepMounted ? { 'aria-controls': panelId(baseId, value) } : {})}
      {...rest}
      id={tabId(baseId, value)}
      role="tab"
      aria-selected={selected}
      // The strip is one tab stop. See the component comment.
      tabIndex={selected ? 0 : -1}
      data-value={value}
      data-state={selected ? 'active' : 'inactive'}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) select(value);
      }}
      onKeyDown={handleKeyDown}
      className={cn(tabVariants({ size }), className)}
    >
      {children}
    </button>
  );
});

/* -------------------------------------------------------------------------- */
/* panel                                                                       */
/* -------------------------------------------------------------------------- */

export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** Ties this panel to the `Tab` with the same value. */
  value: string;
}

/**
 * What a tab reveals. Unselected panels are unmounted, or — with
 * `keepMounted` on the `Tabs` — hidden with the `hidden` attribute *and*
 * `display: none` as an inline style, so a `flex` or `grid` utility on the
 * panel cannot quietly un-hide it and leave the content readable to a screen
 * reader while the tab beside it says otherwise.
 *
 * The panel is focusable. It is the target of Tab from the strip, and a panel
 * that scrolls has to be reachable by keyboard alone (WCAG 2.1.1) — the same
 * reason `TableContainer` takes a tab stop. Pass `tabIndex={-1}` to opt out.
 */
export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(function TabPanel(
  { value, className, style, children, ...rest },
  ref,
) {
  const { baseId, value: selectedValue, keepMounted } = useTabsContext('TabPanel');
  const selected = selectedValue === value;

  if (!selected && !keepMounted) return null;

  const hiddenStyle: CSSProperties | undefined = selected
    ? style
    : { ...style, display: 'none' };

  return (
    <div
      tabIndex={0}
      {...rest}
      ref={ref}
      id={panelId(baseId, value)}
      role="tabpanel"
      aria-labelledby={tabId(baseId, value)}
      hidden={!selected}
      style={hiddenStyle}
      data-state={selected ? 'active' : 'inactive'}
      className={cn('min-w-0 rounded-sm', focusRing, className)}
    >
      {children}
    </div>
  );
});
